// TESSERAX — SPATIALISATION STÉRÉO DU PUITS (cœur pur, testé).
//
// Le puits est ORBITABLE : la caméra tourne autour du centre (azimut `camTheta`).
// Un panoramique stéréo figé sur l'axe X de la grille « ment » dès qu'on tourne —
// une case à GAUCHE de l'écran pouvait s'entendre à droite. On projette donc la
// position d'une case sur l'AXE DROITE-ÉCRAN de la caméra : ce qui est à gauche de
// l'œil s'entend à gauche, quel que soit l'angle d'orbite. Le puits devient un
// instrument que l'oreille sait LIRE — un clear dans un coin, une Bascule vers un
// mur s'entendent VENIR de leur direction réelle (cf. DA « puits = instrument »).
//
// Géométrie (repère THREE, y haut) : la caméra regarde le centre depuis l'azimut θ.
// Vecteur DROITE-écran (horizontal) s = (cos θ, 0, −sin θ) — dérivé de
// s = cross(regard, haut) avec regard = −(sin θ, 0, cos θ). Le pan d'une case est
// la projection de son décalage horizontal (relatif au centre du puits) sur s.
//
// Pur : aucune dépendance THREE / DOM / RNG / WebAudio. Le déterminisme du défi du
// jour reste intact (la spatialisation n'altère ni le tirage ni le score).

// Murs latéraux du puits, indexés comme `Dir` dans core/grid (la pile glisse VERS eux).
export type Wall = 'nx' | 'px' | 'nz' | 'pz';

// Pan stéréo ∈ [−scale, +scale] d'une case (x, z) du puits N×N, vue depuis l'azimut
// caméra `theta`. −1 = oreille gauche, +1 = oreille droite. `scale` borne la largeur
// (un panoramique total fatiguerait — on reste « subtil mais lisible »). Une case au
// centre rend 0. Hors-grille → projection bornée à ±scale (jamais au-delà).
export function panForCell(x: number, z: number, gridN: number, theta: number, scale = 0.6): number {
  const half = (gridN - 1) / 2;
  if (!(half > 0)) return 0;                       // grille 1×1 (ou dégénérée) → pas de largeur
  const ox = x - half, oz = z - half;              // décalage horizontal depuis le centre
  const raw = (ox * Math.cos(theta) - oz * Math.sin(theta)) / half;   // projection sur l'axe droite-écran
  return Math.max(-1, Math.min(1, raw)) * scale;
}

// Pan stéréo d'un MUR latéral (centre de paroi), vu depuis l'azimut `theta`. Sert au
// grondement de Bascule : le mur vers lequel la pile glisse s'entend de SON côté.
export function panForWall(dir: Wall, gridN: number, theta: number, scale = 0.6): number {
  const max = gridN - 1, c = max / 2;
  const x = dir === 'px' ? max : dir === 'nx' ? 0 : c;
  const z = dir === 'pz' ? max : dir === 'nz' ? 0 : c;
  return panForCell(x, z, gridN, theta, scale);
}
